import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import * as FormActions from './form.actions';
import {
  mergeMap,
  exhaustMap,
  switchMap,
  catchError,
  filter,
  map,
  of,
  from,
} from 'rxjs';
import { SubmitService } from '../submit/submit.service';
import { Store } from '@ngrx/store';
import {
  selectCdexTask,
  selectOrderReference,
  selectPasClaimIdentifier,
  selectQr,
} from './form.selectors';
import { NavigationExtras, Router } from '@angular/router';
import { MessageBoxState } from '../models/MessageBoxState';
import { LoggerService } from '../services/logger.service';

@Injectable()
export class SubmitEffects {
  private readonly actions = inject(Actions);
  private readonly submitService = inject(SubmitService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);

  signQr$ = createEffect(() => {
    return this.actions.pipe(
      ofType(FormActions.signQr),
      concatLatestFrom(() => this.store.select(selectQr)),
      mergeMap(([action, stateQr]) =>
        this.submitService.signAndSaveQr(stateQr, action.signature).pipe(
          concatLatestFrom(() => this.store.select(selectCdexTask)),
          map(([qr, task]) => {
            if (task) return FormActions.submitAttachment({ task });
            else return FormActions.createPasBundle({ qr });
          }),
          catchError((error) => of(FormActions.submitFailure({ error }))),
        ),
      ),
    );
  });

  createPasBundle$ = createEffect(() => {
    return this.actions.pipe(
      ofType(FormActions.createPasBundle),
      mergeMap((action) =>
        from(this.submitService.createPasBundle(action.qr)).pipe(
          map((bundle) => FormActions.submitPasBundle({ bundle })),
          catchError((error) => of(FormActions.submitFailure({ error }))),
        ),
      ),
    );
  });

  submitPasBundle$ = createEffect(() => {
    return this.actions.pipe(
      ofType(FormActions.submitPasBundle),
      mergeMap((action) =>
        from(this.submitService.submitPasBundle(action.bundle)).pipe(
          map((response) =>
            FormActions.processSubmitResponse({
              response,
              bundle: action.bundle,
            }),
          ),
          catchError((error) => of(FormActions.submitFailure({ error }))),
        ),
      ),
    );
  });

  processSubmitResponse$ = createEffect(() => {
    return this.actions.pipe(
      ofType(FormActions.processSubmitResponse),
      mergeMap((action) =>
        from(
          this.submitService.processSubmitResponse(
            action.response,
            action.bundle,
          ),
        ).pipe(
          map(() => FormActions.submitPasBundleSuccess()),
          catchError((error) => {
            this.logger.error('Failed to process PAS submit response:', error);
            return of(FormActions.submitPasBundleSuccess());
          }),
        ),
      ),
    );
  });

  submitPasBundleSuccess$ = createEffect(() => {
    return this.actions.pipe(
      ofType(FormActions.submitPasBundleSuccess),
      mergeMap(() => {
        const navigationExtras: NavigationExtras = {
          state: MessageBoxState.ClaimSubmitted,
          skipLocationChange: true,
        };
        this.router.navigateByUrl('message-box', navigationExtras);
        return of(FormActions.resetState());
      }),
    );
  });

  inquireClaim$ = createEffect(() => {
    return this.actions.pipe(
      ofType(FormActions.inquireClaim),
      concatLatestFrom(() => [
        this.store.select(selectOrderReference),
        this.store.select(selectPasClaimIdentifier),
      ]),
      exhaustMap(([, orderReference, claimIdentifier]) =>
        from(
          this.submitService.inquireClaim(orderReference, claimIdentifier),
        ).pipe(
          map(({ claim, claimResponse }) =>
            FormActions.inquireClaimSuccess({ claim, claimResponse }),
          ),
          catchError((error) => {
            this.logger.error('Failed to inquire PAS claim status:', error);
            return of(FormActions.inquireClaimFailure({ error }));
          }),
        ),
      ),
    );
  });

  submissionStatusOnLaunch$ = createEffect(() => {
    return this.actions.pipe(
      ofType(FormActions.setInitialValues),
      filter(({ pasClaimIdentifier }) => !!pasClaimIdentifier),
      map(() => FormActions.loadSubmissionStatus()),
    );
  });

  loadSubmissionStatus$ = createEffect(() => {
    return this.actions.pipe(
      ofType(FormActions.loadSubmissionStatus),
      concatLatestFrom(() => this.store.select(selectPasClaimIdentifier)),
      switchMap(([, claimIdentifier]) =>
        from(this.submitService.getSubmissionStatus(claimIdentifier)).pipe(
          map(({ claim, claimResponse }) =>
            FormActions.loadSubmissionStatusSuccess({ claim, claimResponse }),
          ),
          catchError((error) => {
            this.logger.error('Failed to load PAS submission status:', error);
            return of(FormActions.loadSubmissionStatusFailure({ error }));
          }),
        ),
      ),
    );
  });

  submitAttachment$ = createEffect(() => {
    return this.actions.pipe(
      ofType(FormActions.submitAttachment),
      mergeMap((action) =>
        from(this.submitService.submitAttachment(action.task)).pipe(
          map(() => FormActions.submitAttachmentSuccess()),
          catchError((error) => of(FormActions.submitFailure({ error }))),
        ),
      ),
    );
  });

  submitAttachmentSuccess$ = createEffect(() => {
    return this.actions.pipe(
      ofType(FormActions.submitAttachmentSuccess),
      mergeMap(() => {
        const navigationExtras: NavigationExtras = {
          state: MessageBoxState.CdexSubmitted,
          skipLocationChange: true,
        };
        this.router.navigateByUrl('message-box', navigationExtras);
        return of(FormActions.resetState());
      }),
    );
  });
}
